import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Fade,
  Backdrop,
  Chip,
  Button,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  CheckCircleOutline as ApprovedIcon,
  Cancel as RejectedIcon,
  Block as CancelledIcon,
} from '@mui/icons-material';
import { InquiryService } from '../../services/inquiryService';

interface ProfileVisibilityPopupProps {
  open: boolean;
  onClose: () => void;
  isPublic: boolean;
  onToggleVisibility?: () => void;
  isOwnProfile?: boolean;
  profileUserId?: string | null;
  currentUserId?: string | null;
}

const ProfileVisibilityPopup: React.FC<ProfileVisibilityPopupProps> = ({
  open,
  onClose,
  isPublic,
  onToggleVisibility,
  isOwnProfile = false,
  profileUserId,
  currentUserId,
}) => {
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [isSubmittingInquiry, setIsSubmittingInquiry] = useState(false);
  const [inquirySubmitted, setInquirySubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingInquiry, setExistingInquiry] = useState<{
    status: 'pending' | 'approved' | 'rejected' | 'cancelled';
    date: string;
  } | null>(null);

  // Check for existing inquiry when popup opens
  React.useEffect(() => {
    if (open && profileUserId && currentUserId && !isOwnProfile) {
      const checkExistingInquiry = async () => {
        try {
          // Check localStorage first
          const localStorageKey = `inquiry_${currentUserId}_${profileUserId}`;
          const storedInquiry = localStorage.getItem(localStorageKey);
          
          if (storedInquiry) {
            const inquiryData = JSON.parse(storedInquiry);
            setExistingInquiry({
              status: inquiryData.status,
              date: inquiryData.date
            });
            return;
          }

          // If not in localStorage, check database
          const hasPending = await InquiryService.hasPendingInquiry(profileUserId, currentUserId);
          if (hasPending) {
            // Get full inquiry details
            const inquiries = await InquiryService.getInquiriesByUser(currentUserId);
            const inquiry = inquiries.find(i => 
              i.profile_user_id === profileUserId && 
              i.status === 'pending'
            );
            
            if (inquiry) {
              const inquiryData = {
                status: inquiry.status,
                date: inquiry.created_at
              };
              setExistingInquiry(inquiryData);
              
              // Store in localStorage for future reference
              localStorage.setItem(localStorageKey, JSON.stringify(inquiryData));
            }
          }
        } catch (error) {
          console.error('Error checking existing inquiry:', error);
        }
      };

      checkExistingInquiry();
    }
  }, [open, profileUserId, currentUserId, isOwnProfile]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleSubmitInquiry = async () => {
    if (!profileUserId || !currentUserId) {
      setError('Missing user information');
      return;
    }

    setIsSubmittingInquiry(true);
    setError(null);

    try {
      const result = await InquiryService.createInquiry(profileUserId, inquiryMessage);
      
      if (result.success) {
        setInquirySubmitted(true);
        setInquiryMessage('');
        
        // Save to localStorage
        const localStorageKey = `inquiry_${currentUserId}_${profileUserId}`;
        const inquiryData: {
          status: 'pending' | 'approved' | 'rejected' | 'cancelled';
          date: string;
        } = {
          status: 'pending',
          date: new Date().toISOString()
        };
        localStorage.setItem(localStorageKey, JSON.stringify(inquiryData));
        
        // Update existing inquiry state
        setExistingInquiry(inquiryData);
      } else {
        setError(result.error || 'Failed to submit inquiry');
      }
    } catch (error) {
      setError('An unexpected error occurred');
      console.error('Error submitting inquiry:', error);
    } finally {
      setIsSubmittingInquiry(false);
    }
  };

  const handleClose = () => {
    setInquiryMessage('');
    setInquirySubmitted(false);
    setError(null);
    setExistingInquiry(null);
    onClose();
  };

  return (
    <Backdrop
      open={open}
      onClick={handleBackdropClick}
      sx={{
        zIndex: 1300,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Fade in={open}>
        <Card
          sx={{
            maxWidth: 400,
            width: '90%',
            borderRadius: 4,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            mx: 'auto',
            my: 2,
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          {/* Decorative background pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 120,
              height: 120,
              background: isPublic 
                ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              borderRadius: '50%',
              transform: 'translate(30px, -30px)',
            }}
          />
          
          {/* Close button */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(8px)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <CardContent sx={{ p: 4, pt: 3 }}>
            {/* Header with icon and status */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isPublic 
                    ? 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)'
                    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  boxShadow: isPublic 
                    ? '0 8px 32px rgba(34, 197, 94, 0.3)'
                    : '0 8px 32px rgba(239, 68, 68, 0.3)',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      boxShadow: isPublic 
                        ? '0 8px 32px rgba(34, 197, 94, 0.3)'
                        : '0 8px 32px rgba(239, 68, 68, 0.3)',
                    },
                    '50%': {
                      boxShadow: isPublic 
                        ? '0 8px 32px rgba(34, 197, 94, 0.5)'
                        : '0 8px 32px rgba(239, 68, 68, 0.5)',
                    },
                    '100%': {
                      boxShadow: isPublic 
                        ? '0 8px 32px rgba(34, 197, 94, 0.3)'
                        : '0 8px 32px rgba(239, 68, 68, 0.3)',
                    },
                  },
                }}
              >
                {isPublic ? (
                  <PublicIcon sx={{ fontSize: 40, color: 'white' }} />
                ) : (
                  <LockIcon sx={{ fontSize: 40, color: 'white' }} />
                )}
              </Box>

              <Typography
                variant="h5"
                fontWeight={800}
                sx={{
                  background: isPublic 
                    ? 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)'
                    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textAlign: 'center',
                }}
              >
                {isPublic ? 'Profile is Public' : 'Profile is Private'}
              </Typography>

              <Chip
                icon={isPublic ? <VisibilityIcon /> : <VisibilityOffIcon />}
                label={isPublic ? 'Visible to Everyone' : 'Only You Can See'}
                color={isPublic ? 'success' : 'error'}
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem',
                  borderWidth: 2,
                  '& .MuiChip-icon': {
                    fontSize: '1.1rem',
                  },
                }}
              />
            </Box>

            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                lineHeight: 1.6,
                mb: 3,
                px: 1,
              }}
            >
              {isOwnProfile ? (
                isPublic ? (
                  <>
                    Your profile is <strong>publicly visible</strong> and can be discovered by employers and recruiters. 
                    This helps you get more job opportunities and networking connections.
                  </>
                ) : (
                  <>
                    Your profile is <strong>private</strong> and only visible to you. 
                    Employers and recruiters cannot see your profile or contact you.
                  </>
                )
              ) : (
                isPublic ? (
                  <>
                    This profile is <strong>publicly visible</strong> and can be viewed by anyone.
                  </>
                ) : (
                  <>
                    This profile is <strong>private</strong> and not accessible to the public. 
                    You can send an inquiry to request access.
                  </>
                )
              )}
            </Typography>

            {/* Error message */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Success message for inquiry */}
            {inquirySubmitted && (
              <Alert 
                severity="success" 
                sx={{ mb: 2 }}
                icon={<CheckCircleIcon />}
              >
                Your inquiry has been sent! The profile owner will be notified and can approve or reject your request.
              </Alert>
            )}

            {/* Existing inquiry status */}
            {existingInquiry && !inquirySubmitted && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  p: 3,
                  mb: 2,
                  borderRadius: 3,
                  background: existingInquiry.status === 'approved' 
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)'
                    : existingInquiry.status === 'rejected'
                    ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)'
                    : existingInquiry.status === 'cancelled'
                    ? 'linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(75, 85, 99, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
                  border: existingInquiry.status === 'approved'
                    ? '1px solid rgba(34, 197, 94, 0.2)'
                    : existingInquiry.status === 'rejected'
                    ? '1px solid rgba(239, 68, 68, 0.2)'
                    : existingInquiry.status === 'cancelled'
                    ? '1px solid rgba(107, 114, 128, 0.2)'
                    : '1px solid rgba(251, 191, 36, 0.2)',
                }}
              >
                {existingInquiry.status === 'approved' && (
                  <ApprovedIcon sx={{ fontSize: 32, color: '#22c55e' }} />
                )}
                {existingInquiry.status === 'rejected' && (
                  <RejectedIcon sx={{ fontSize: 32, color: '#ef4444' }} />
                )}
                {existingInquiry.status === 'cancelled' && (
                  <CancelledIcon sx={{ fontSize: 32, color: '#6b7280' }} />
                )}
                {existingInquiry.status === 'pending' && (
                  <PendingIcon sx={{ fontSize: 32, color: '#fbbf24' }} />
                )}
                
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      color: existingInquiry.status === 'approved'
                        ? '#22c55e'
                        : existingInquiry.status === 'rejected'
                        ? '#ef4444'
                        : existingInquiry.status === 'cancelled'
                        ? '#6b7280'
                        : '#fbbf24',
                      mb: 0.5,
                    }}
                  >
                    {existingInquiry.status === 'approved' && 'Inquiry Approved!'}
                    {existingInquiry.status === 'rejected' && 'Inquiry Rejected'}
                    {existingInquiry.status === 'cancelled' && 'Inquiry Cancelled'}
                    {existingInquiry.status === 'pending' && 'Inquiry Pending'}
                  </Typography>
                  
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                    }}
                  >
                    {existingInquiry.status === 'approved' && 'You can now view this profile!'}
                    {existingInquiry.status === 'rejected' && 'The profile owner declined your request.'}
                    {existingInquiry.status === 'cancelled' && 'This inquiry was cancelled.'}
                    {existingInquiry.status === 'pending' && `Sent on ${new Date(existingInquiry.date).toLocaleDateString()}`}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Inquiry form for private profiles (non-own) */}
            {!isOwnProfile && !isPublic && !inquirySubmitted && !existingInquiry && (
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Send a message explaining why you'd like to view this profile..."
                  value={inquiryMessage}
                  onChange={(e) => setInquiryMessage(e.target.value)}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmitInquiry}
                  disabled={isSubmittingInquiry}
                  startIcon={isSubmittingInquiry ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    fontWeight: 700,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: 'grey.300',
                      color: 'grey.500',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {isSubmittingInquiry ? 'Sending Inquiry...' : 'Send Inquiry'}
                </Button>
              </Box>
            )}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              {isOwnProfile && onToggleVisibility && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={onToggleVisibility}
                  sx={{
                    background: isPublic 
                      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                      : 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                    color: 'white',
                    fontWeight: 700,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: isPublic 
                      ? '0 4px 16px rgba(239, 68, 68, 0.3)'
                      : '0 4px 16px rgba(34, 197, 94, 0.3)',
                    '&:hover': {
                      boxShadow: isPublic 
                        ? '0 6px 20px rgba(239, 68, 68, 0.4)'
                        : '0 6px 20px rgba(34, 197, 94, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {isPublic ? 'Make Profile Private' : 'Make Profile Public'}
                </Button>
              )}

              <Button
                variant="outlined"
                fullWidth
                onClick={handleClose}
                sx={{
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': {
                    borderColor: 'grey.400',
                    backgroundColor: 'grey.50',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {inquirySubmitted ? 'Close' : 'Got it'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    </Backdrop>
  );
};

export default ProfileVisibilityPopup;
